import { isInternalUserId, type AuthMethod } from './events'
import { isProductAnalyticsConfigured, type ProductAnalyticsConfig } from './product-analytics'

interface AnalyticsLifecycleClient {
  getPersistedUserId(): string | undefined
  hasPendingReset(): boolean
  identify(userId: string): boolean
  init(config: ProductAnalyticsConfig): boolean
  invalidateIdentity(): void
  pageView(pathname: string): void
  reset(): boolean
  userAuthenticated(authMethod: AuthMethod): void
}

interface AnalyticsLifecycleState {
  authMethod?: AuthMethod
  config: ProductAnalyticsConfig
  pathname?: string
  userId?: string
}

export type AnalyticsLifecycleSyncResult = 'consume' | 'disabled' | 'discard' | 'preserve' | 'retry'

const hasAnalyticsConfigChanged = (
  previousConfig: ProductAnalyticsConfig | undefined,
  config: ProductAnalyticsConfig
): boolean =>
  previousConfig === undefined ||
  previousConfig.appVersion !== config.appVersion ||
  previousConfig.enabled !== config.enabled ||
  previousConfig.environment !== config.environment ||
  previousConfig.errorReportingEnabled !== config.errorReportingEnabled ||
  previousConfig.host !== config.host ||
  previousConfig.isImpersonating !== config.isImpersonating ||
  previousConfig.projectToken !== config.projectToken

export const createAnalyticsLifecycle = (analytics: AnalyticsLifecycleClient) => {
  let isForcedResetPending = false
  let isAuthenticationCaptured = false
  let pendingAuthMethod: AuthMethod | undefined
  let previousState: AnalyticsLifecycleState | undefined

  const resetIdentity = () => {
    const isResetSuccessful = analytics.reset()
    if (!isResetSuccessful) {
      return false
    }
    isAuthenticationCaptured = false
    return true
  }

  const identifyUser = ({ userId }: AnalyticsLifecycleState): boolean => {
    if (!isInternalUserId(userId)) {
      return false
    }

    const isIdentified = analytics.identify(userId)
    if (!isIdentified) {
      return false
    }
    if (!pendingAuthMethod || isAuthenticationCaptured) {
      return true
    }

    analytics.userAuthenticated(pendingAuthMethod)
    pendingAuthMethod = undefined
    isAuthenticationCaptured = true
    return true
  }

  const preserveResetEvidenceForDisabledCollection = (state: AnalyticsLifecycleState): void => {
    const userLoggedOut = Boolean(previousState?.userId) && state.userId === undefined
    if (userLoggedOut) {
      analytics.reset()
      return
    }
    if (previousState !== undefined || state.userId !== undefined) {
      return
    }

    try {
      if (analytics.getPersistedUserId()) {
        analytics.reset()
      }
    } catch {
      analytics.reset()
    }
  }

  return {
    suspendIdentity() {
      analytics.invalidateIdentity()
      isForcedResetPending = true
    },

    sync(state: AnalyticsLifecycleState): AnalyticsLifecycleSyncResult {
      const isCollectionConfigured = isProductAnalyticsConfigured(state.config)
      if (isCollectionConfigured && state.authMethod) {
        pendingAuthMethod = state.authMethod
      }

      if (hasAnalyticsConfigChanged(previousState?.config, state.config)) {
        if (!analytics.init(state.config) && isCollectionConfigured) {
          return 'retry'
        }
      }

      if (!isCollectionConfigured) {
        analytics.invalidateIdentity()
        isForcedResetPending = true
        pendingAuthMethod = undefined
        preserveResetEvidenceForDisabledCollection(state)
        previousState = state
        return 'disabled'
      }

      if (state.config.isImpersonating) {
        pendingAuthMethod = undefined
        previousState = state
        return 'discard'
      }
      if (state.userId !== undefined && !isInternalUserId(state.userId)) {
        analytics.invalidateIdentity()
        pendingAuthMethod = undefined
        previousState = state
        return 'discard'
      }

      let resetCompletedBeforeSync = false
      if (isForcedResetPending || analytics.hasPendingReset()) {
        if (!resetIdentity()) {
          return 'retry'
        }
        isForcedResetPending = false
        resetCompletedBeforeSync = true
      }

      if (previousState === undefined) {
        let persistedUserId: string | undefined
        try {
          persistedUserId = analytics.getPersistedUserId()
        } catch {
          return 'retry'
        }
        if (state.userId) {
          if (!resetCompletedBeforeSync && persistedUserId && persistedUserId !== state.userId) {
            if (!resetIdentity()) {
              return 'retry'
            }
          }
          if (!identifyUser(state)) {
            return 'retry'
          }
        } else if (!resetCompletedBeforeSync && persistedUserId) {
          if (!resetIdentity()) {
            return 'retry'
          }
        }
        if (state.pathname) {
          analytics.pageView(state.pathname)
        }
        previousState = state
        return state.userId ? 'consume' : 'preserve'
      }

      if (previousState.config.isImpersonating) {
        if (!resetCompletedBeforeSync && !resetIdentity()) {
          return 'retry'
        }
        if (state.userId && !identifyUser(state)) {
          return 'retry'
        }
        if (state.pathname) {
          analytics.pageView(state.pathname)
        }
        previousState = state
        return state.userId ? 'consume' : 'preserve'
      }

      const userIdChanged = previousState.userId !== state.userId
      const identityNeedsInitialization = userIdChanged || resetCompletedBeforeSync
      if (identityNeedsInitialization) {
        if (userIdChanged && previousState.userId && !resetCompletedBeforeSync) {
          if (!resetIdentity()) {
            return 'retry'
          }
        }
        if (state.userId && !identifyUser(state)) {
          return 'retry'
        }
      }

      if (
        state.pathname &&
        (previousState.pathname !== state.pathname || (identityNeedsInitialization && state.userId))
      ) {
        analytics.pageView(state.pathname)
      }

      previousState = state
      return state.userId ? 'consume' : 'preserve'
    },
  }
}
